/**
 * AdminUsersPage — Tela A1 Gestão Usuários (Brief 5 A1, W3.1).
 *
 * 4 abas canônicas:
 *   1. Lista — tabela paginada + filtros + bulk deactivate.
 *   2. Detalhe — drawer com perfil + edição + auditoria.
 *   3. Hierarquia — tree reportsTo.
 *   4. Convites — list InviteTokens + formulário POST.
 *
 * Pattern Opção A: capability gate silencioso em cada aba.
 * D-A 3-camadas: tierAllows × roleHasCapability × userLifecycle aplicado
 *   via useCapability() + useAuthStore (backend já filtrou capabilities por
 *   tier/role/lifecycle em GET /v1/auth/me).
 * D-B: cada ação destrutiva tem gate próprio no seu componente.
 *
 * Solo tier: EmptyState com upgrade CTA (users-read tierMin=team).
 */

import { useState } from 'react';

import { useCapability } from '../../../hooks/use-capability';

import { UserDetailDrawer } from './UserDetailDrawer';
import { UsersHierarchyTab } from './UsersHierarchyTab';
import { UsersInvitesTab } from './UsersInvitesTab';
import { UsersListTab } from './UsersListTab';

import type { UserWireItem } from './types';

type TabId = 'lista' | 'hierarquia' | 'convites';

const TABS: { id: TabId; label: string }[] = [
  { id: 'lista', label: 'Lista' },
  { id: 'hierarquia', label: 'Hierarquia' },
  { id: 'convites', label: 'Convites' },
];

export function AdminUsersPage(): JSX.Element {
  const canRead = useCapability('users-read');
  const [activeTab, setActiveTab] = useState<TabId>('lista');
  const [selectedUser, setSelectedUser] = useState<UserWireItem | null>(null);

  // Solo tier (+ teams abaixo de team): users-read ausente → upgrade CTA.
  if (!canRead) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
        <div className="rounded-2xl border border-border-subtle bg-surface p-8 max-w-md">
          <span className="text-5xl">👥</span>
          <h2 className="mt-4 text-lg font-semibold text-text-primary">Gestão de Usuários</h2>
          <p className="mt-2 text-sm text-text-secondary">
            Esta funcionalidade requer o pacote <strong>Team</strong> ou superior.
          </p>
          <p className="mt-1 text-xs text-text-secondary">
            Fale com o administrador do tenant para liberar a gestão de usuários.
          </p>
          <button
            type="button"
            className="mt-6 rounded bg-indigo-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-indigo-700"
          >
            Ver planos disponíveis
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Page header */}
      <div className="border-b border-border-subtle bg-surface px-8 py-5">
        <h1 className="text-xl font-semibold text-text-primary">Gestão de Usuários</h1>
        <p className="mt-0.5 text-sm text-text-secondary">
          Gerencie os membros da equipe, hierarquia e convites.
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-border-subtle bg-surface px-8">
        <nav className="flex gap-1" role="tablist" aria-label="Abas de gestão de usuários">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={[
                'relative px-4 py-3 text-sm font-medium transition-colors',
                activeTab === tab.id
                  ? 'text-indigo-600 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-indigo-600'
                  : 'text-text-secondary hover:text-text-primary',
              ].join(' ')}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-auto px-8 py-6">
        {activeTab === 'lista' && <UsersListTab onSelectUser={(u) => setSelectedUser(u)} />}
        {activeTab === 'hierarquia' && (
          <UsersHierarchyTab defaultUserId={selectedUser?.id ?? null} />
        )}
        {activeTab === 'convites' && <UsersInvitesTab />}
      </div>

      {/* Detalhe drawer — abre quando usuario selecionado na lista */}
      {selectedUser !== null && (
        <UserDetailDrawer userId={selectedUser.id} onClose={() => setSelectedUser(null)} />
      )}
    </div>
  );
}
